<template>
  <transition name="fade-slide">
    <div
      v-if="showNotification"
      class="notification-center"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div class="notification-card">
        <div class="notification-icon" aria-hidden="true"><i class="fas fa-envelope-open-text"></i></div>
        <div class="notification-text">
          <h4>{{ $t('notifications.emailVerification') }}</h4>
          <p>{{ $t('notifications.emailVerificationMessage') }}</p>
          <p class="check-email">{{ $t('notifications.checkEmail') }}</p>
        </div>
        <p v-if="feedbackMessage" class="notification-feedback" role="status" aria-live="polite">
          {{ feedbackMessage }}
        </p>
        <button 
          type="button"
          @click="resendVerification" 
          :disabled="resendLoading"
          class="resend-button"
        >
          <span v-if="resendLoading">{{ $t('notifications.sending') }}</span>
          <span v-else>{{ $t('notifications.resendEmail') }}</span>
        </button>
        <button
          type="button"
          @click="close"
          class="notification-close"
          :aria-label="t('a11y.closeNotification')"
        >
          &times;
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useUserStore } from '@/stores/user';
import { useI18n } from '@/composables/useI18n';
import api from '@/api';

const { t } = useI18n();
const userStore = useUserStore();
const resendLoading = ref(false);
const dismissed = ref(false);
const feedbackMessage = ref('');

const showNotification = computed(() => {
  // Показывать, если пользователь есть, не верифицирован и не скрыто
  return userStore.user && !userStore.user.is_verified && !dismissed.value;
});

const resendVerification = async () => {
  if (!userStore.user?.email) return;
  resendLoading.value = true;
  feedbackMessage.value = '';
  try {
    // Используем новый метод для аутентифицированных пользователей
    await api.resendVerificationAuthenticated();
    feedbackMessage.value = t('notifications.emailSent');
  } catch (error) {
    console.error('Ошибка при повторной отправке:', error);
    feedbackMessage.value = t('errors.networkError');
  } finally {
    resendLoading.value = false;
  }
};

function close() {
  dismissed.value = true;
  localStorage.setItem('email_warning_dismissed', 'true');
}

// Следим за изменением пользователя (например, после логина)
watch(
  () => userStore.user,
  (newUser, oldUser) => {
    if (!newUser) {
      // При разлогине сбрасываем dismissed и удаляем ключ из localStorage
      dismissed.value = false;
      localStorage.removeItem('email_warning_dismissed');
    } else {
      dismissed.value = !!localStorage.getItem('email_warning_dismissed');
    }
  },
  { immediate: true }
);

onMounted(() => {
  dismissed.value = !!localStorage.getItem('email_warning_dismissed');
});
</script>

<style scoped>
.notification-center {
  position: fixed;
  top: 40px;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.notification-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: rgba(30, 32, 38, 0.85);
  color: #fff;
  border-radius: 18px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  padding: 28px 36px;
  min-width: 0;
  width: min(480px, calc(100vw - 2rem));
  max-width: 700px;
  pointer-events: auto;
  gap: 20px;
  position: relative;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255,255,255,0.08);
  animation: pop-in 0.5s;
  margin: 0 1rem;
}

@keyframes pop-in {
  from { transform: translateY(-30px) scale(0.98); opacity: 0; }
  to   { transform: translateY(0) scale(1); opacity: 1; }
}

.notification-icon {
  font-size: 2.3rem;
  flex-shrink: 0;
  margin-right: 14px;
  color: #ffd666;
  filter: drop-shadow(0 2px 8px #ffd66655);
}

.notification-text {
  width: 100%;
}

.notification-title {
  font-weight: 700;
  font-size: 1.18rem;
  margin-bottom: 7px;
  color: #fff;
}

.notification-message {
  font-size: 1.02rem;
  opacity: 0.93;
  line-height: 1.5;
  color: #e6e6e6;
  display: block;
  margin-bottom: 18px;
}

.notification-feedback {
  margin: 0;
  font-size: 0.95rem;
  color: #ffd666;
  line-height: 1.4;
}

.resend-button {
  background: transparent;
  color: #ffd666;
  border: 1.5px solid #ffd666;
  border-radius: 8px;
  padding: 10px 22px;
  font-weight: 600;
  font-size: 1rem;
  margin: 0;
  margin-top: 6px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(255, 229, 143, 0.10);
  align-self: flex-start;
  min-height: 44px;
}
.resend-button:hover:not(:disabled) {
  background: #ffd666;
  color: #222;
}

.resend-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.notification-close {
  background: none;
  border: none;
  color: #fff;
  font-size: 1.7rem;
  cursor: pointer;
  opacity: 0.4;
  transition: opacity 0.2s;
  position: absolute;
  top: 14px;
  right: 22px;
}
.notification-close:hover {
  opacity: 1;
}

.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.4s cubic-bezier(.4,0,.2,1);
}
.fade-slide-enter-from, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}
</style> 